import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

// Parse participant _id from QR output
// QR value: http://localhost:3002/checkin/<participantId>
// Also accept plain 24-char hex IDs or registrationIds
const parseParticipantId = (input) => {
    const trimmed = input.trim();
    try {
        const url = new URL(trimmed);
        // /checkin/:id path
        const match = url.pathname.match(/\/checkin\/([a-f\d]{24})/i);
        if (match) return match[1];
        // fallback: last path segment
        const segments = url.pathname.split('/').filter(Boolean);
        if (segments.length > 0) return segments[segments.length - 1];
    } catch {}
    return trimmed;
};

const EventScanPageV2 = () => {
    // When opened via QR code URL (/v2/checkin/:participantId), auto-scan after login
    const { participantId: urlParticipantId } = useParams();

    const [authenticated, setAuthenticated] = useState(false);
    const [credentials, setCredentials] = useState({ username: '', password: '' });
    const [eventInfo, setEventInfo] = useState(null);
    const [adminRole, setAdminRole] = useState('full');

    const [scanInput, setScanInput] = useState('');
    const [scanning, setScanning] = useState(false);
    const [scanResult, setScanResult] = useState(null);
    const [scanError, setScanError] = useState('');
    const [redeemLoading, setRedeemLoading] = useState(null);

    const [loginForm, setLoginForm] = useState({ username: '', password: '' });
    const [loginLoading, setLoginLoading] = useState(false);
    const [loginError, setLoginError] = useState('');

    const inputRef = useRef(null);
    const STORAGE_KEY = 'v2ScanAuth';

    useEffect(() => {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                setCredentials({ username: parsed.username, password: parsed.password });
                setEventInfo(parsed.eventInfo || null);
                setAdminRole(parsed.role || 'full');
                setAuthenticated(true);
            } catch {}
        }
    }, []);

    // Auto-scan if opened from QR code URL
    useEffect(() => {
        if (authenticated && urlParticipantId) {
            handleScan(urlParticipantId);
        }
    }, [authenticated]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (authenticated && !urlParticipantId) {
            setTimeout(() => inputRef.current?.focus(), 150);
        }
    }, [authenticated, scanResult]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginError('');
        setLoginLoading(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/v2/scan/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(loginForm)
            });
            const data = await res.json();
            if (data.success) {
                const auth = {
                    username: loginForm.username,
                    password: loginForm.password,
                    role: data.role || 'full',
                    eventInfo: { id: data.eventId, name: data.eventName }
                };
                localStorage.setItem(STORAGE_KEY, JSON.stringify(auth));
                setCredentials({ username: loginForm.username, password: loginForm.password });
                setEventInfo({ id: data.eventId, name: data.eventName });
                setAdminRole(data.role || 'full');
                setAuthenticated(true);
            } else {
                setLoginError(data.message || 'Invalid credentials');
            }
        } catch {
            setLoginError('Error connecting to server');
        } finally {
            setLoginLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem(STORAGE_KEY);
        setAuthenticated(false);
        setScanResult(null);
        setScanError('');
        setEventInfo(null);
    };

    const handleScan = async (rawInput) => {
        const participantId = parseParticipantId(rawInput);
        if (!participantId) return;

        setScanError('');
        setScanResult(null);
        setScanning(true);
        setScanInput('');

        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/v2/scan/scan`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...credentials, participantId })
            });
            const data = await res.json();
            if (data.success) {
                setScanResult(data);
            } else {
                setScanError(data.message || 'Participant not found');
            }
        } catch {
            setScanError('Error connecting to server');
        } finally {
            setScanning(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && scanInput.trim()) {
            handleScan(scanInput);
        }
    };

    const handleRedeemToggle = async (amenityName, currentlyRedeemed) => {
        const newRedeemed = !currentlyRedeemed;

        if (!newRedeemed && adminRole !== 'full' && adminRole !== 'admin') {
            alert('Only admins can undo amenity redemption');
            return;
        }

        // Add confirmation when undoing
        if (!newRedeemed) {
            const confirmed = window.confirm(`Are you sure you want to undo the redemption for "${amenityName}"?`);
            if (!confirmed) return;
        }

        setRedeemLoading(amenityName);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/v2/scan/amenity/redeem`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...credentials,
                    participantId: scanResult.participant._id,
                    amenityName,
                    redeemed: newRedeemed
                })
            });
            const data = await res.json();
            if (data.success) {
                setScanResult(prev => ({
                    ...prev,
                    participant: { ...prev.participant, amenities: data.amenities }
                }));
            } else {
                alert(data.message || 'Failed to update amenity');
            }
        } catch {
            alert('Error connecting to server');
        } finally {
            setRedeemLoading(null);
        }
    };

    // ---- Login screen ----
    if (!authenticated) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center px-4">
                <div className="w-full max-w-sm">
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-indigo-600 mb-4">
                            <span className="text-white text-2xl">📷</span>
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900">Event Check-in</h1>
                        <p className="text-sm text-gray-500 mt-1">Sign in with your event credentials</p>
                    </div>
                    <div className="bg-white rounded-2xl shadow-md p-6">
                        <form onSubmit={handleLogin} className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                                    Event Username
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={loginForm.username}
                                    onChange={e => setLoginForm(p => ({ ...p, username: e.target.value }))}
                                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    placeholder="e.g. myevent2026"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                                    Password
                                </label>
                                <input
                                    type="password"
                                    required
                                    value={loginForm.password}
                                    onChange={e => setLoginForm(p => ({ ...p, password: e.target.value }))}
                                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                            {loginError && (
                                <p className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">{loginError}</p>
                            )}
                            <button
                                type="submit"
                                disabled={loginLoading}
                                className="w-full py-2.5 bg-indigo-600 text-white font-bold text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-60 transition-colors"
                            >
                                {loginLoading ? 'Signing in...' : 'Sign In'}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    // ---- Main scan interface ----
    return (
        <div className="min-h-screen bg-gray-50">
            {/* Top bar */}
            <div className="bg-indigo-600 text-white px-4 py-3 flex items-center justify-between shadow-md">
                <div className="flex items-center gap-2">
                    <span className="text-lg">📷</span>
                    <div>
                        <div className="font-bold text-sm leading-tight">
                            {eventInfo?.name || 'Event'} — Check-in
                        </div>
                        <div className="text-[10px] text-indigo-200 font-medium">
                            {credentials.username} · {adminRole === 'full' ? 'Full Admin' : 'Check-in'}
                        </div>
                    </div>
                </div>
                <button
                    onClick={handleLogout}
                    className="text-[11px] text-indigo-200 hover:text-white font-medium"
                >
                    Logout
                </button>
            </div>

            <div className="max-w-lg mx-auto px-4 py-6 space-y-4">

                {/* Scan Input */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">
                        Scan QR Code or Enter Participant ID
                    </label>
                    <div className="flex gap-2">
                        <input
                            ref={inputRef}
                            type="text"
                            value={scanInput}
                            onChange={e => setScanInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Point scanner or type ID → press Enter"
                            autoComplete="off"
                            className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl text-sm font-mono focus:outline-none focus:border-indigo-500 transition-colors"
                        />
                        <button
                            onClick={() => scanInput.trim() && handleScan(scanInput)}
                            disabled={scanning || !scanInput.trim()}
                            className="px-4 py-3 bg-indigo-600 text-white text-sm font-bold rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                        >
                            {scanning ? '⏳' : '→'}
                        </button>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-2">
                        Hardware QR scanner types into this field automatically. Press Enter or tap → to process.
                    </p>
                </div>

                {/* Error */}
                {scanError && (
                    <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-2">
                        <span className="text-red-500 text-lg">⚠️</span>
                        <p className="text-sm text-red-700 font-medium">{scanError}</p>
                    </div>
                )}

                {/* Scanning indicator */}
                {scanning && (
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex items-center justify-center gap-3">
                        <div className="animate-spin w-5 h-5 border-2 border-indigo-600 border-t-transparent rounded-full"></div>
                        <span className="text-sm text-gray-500 font-medium">Looking up participant...</span>
                    </div>
                )}

                {/* Scan Result */}
                {scanResult && (
                    <div className={`bg-white rounded-2xl shadow-sm border-2 overflow-hidden transition-all ${scanResult.participant.isCheckedIn ? 'border-green-400' : 'border-gray-200'}`}>

                        {/* Check-in status banner */}
                        {scanResult.autoCheckedIn ? (
                            <div className="bg-green-500 text-white px-4 py-2 flex items-center gap-2">
                                <span className="text-base">✅</span>
                                <span className="text-sm font-bold">Checked In!</span>
                            </div>
                        ) : (
                            <div className="bg-blue-50 border-b border-blue-100 px-4 py-2 flex items-center gap-2">
                                <span className="text-base">ℹ️</span>
                                <span className="text-xs text-blue-700 font-medium">Already checked in previously</span>
                            </div>
                        )}

                        {/* Participant Info */}
                        <div className="p-5">
                            <div className="flex items-start justify-between gap-3 mb-4">
                                <div>
                                    <h2 className="text-xl font-bold text-gray-900 leading-tight">
                                        {scanResult.participant.name}
                                    </h2>
                                    <p className="text-xs font-mono text-gray-500 mt-0.5">
                                        {scanResult.participant.registrationId}
                                    </p>
                                </div>
                                <span className={`shrink-0 px-2.5 py-1 text-[10px] font-bold rounded-full border ${
                                    scanResult.participant.paymentStatus === 'completed'
                                        ? 'bg-green-100 text-green-800 border-green-200'
                                        : 'bg-yellow-100 text-yellow-800 border-yellow-200'
                                }`}>
                                    {(scanResult.participant.paymentStatus || '').toUpperCase()}
                                </span>
                            </div>

                            {/* Category */}
                            {scanResult.participant.categoryName && (
                                <div className="bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-2.5 mb-4 flex items-center gap-2">
                                    <span className="text-indigo-500">🏷️</span>
                                    <div>
                                        <span className="text-[9px] font-bold text-indigo-400 uppercase tracking-wide">Category</span>
                                        <p className="text-sm font-bold text-indigo-800">{scanResult.participant.categoryName}</p>
                                    </div>
                                </div>
                            )}

                            {/* Amenities */}
                            {scanResult.amenities && scanResult.amenities.length > 0 && (
                                <div>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Amenities</p>
                                    <div className="space-y-2">
                                        {scanResult.amenities.map(amenity => {
                                            const state = scanResult.participant.amenities?.[amenity.name];
                                            const isRedeemed = state?.redeemed === true;
                                            const isLoading = redeemLoading === amenity.name;
                                            const canUndo = adminRole === 'full' || adminRole === 'admin';

                                            return (
                                                <div
                                                    key={amenity.name}
                                                    className={`flex items-center justify-between px-4 py-3 rounded-xl border transition-all ${
                                                        isRedeemed
                                                            ? 'bg-green-50 border-green-300'
                                                            : 'bg-gray-50 border-gray-200'
                                                    }`}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <span className={`text-xl ${isRedeemed ? '' : 'grayscale opacity-40'}`}>
                                                            {isRedeemed ? '✅' : '⬜'}
                                                        </span>
                                                        <div>
                                                            <p className={`text-sm font-bold ${isRedeemed ? 'text-green-800' : 'text-gray-700'}`}>
                                                                {amenity.name}
                                                            </p>
                                                            {isRedeemed && state?.at && (
                                                                <p className="text-[10px] text-green-600 mt-0.5">
                                                                    {new Date(state.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                    {state.by && ` · by ${state.by}`}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => handleRedeemToggle(amenity.name, isRedeemed)}
                                                        disabled={isLoading || (isRedeemed && !canUndo)}
                                                        title={isRedeemed && !canUndo ? 'Only full admin can undo' : undefined}
                                                        className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                                            isRedeemed
                                                                ? canUndo
                                                                    ? 'bg-white border border-red-200 text-red-600 hover:bg-red-50'
                                                                    : 'bg-white border border-gray-200 text-gray-400 cursor-not-allowed'
                                                                : 'bg-indigo-600 text-white hover:bg-indigo-700'
                                                        }`}
                                                    >
                                                        {isLoading ? '⏳' : isRedeemed ? (canUndo ? 'Undo' : '🔒') : 'Mark'}
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {scanResult.amenities && scanResult.amenities.length === 0 && (
                                <div className="text-center py-4 text-gray-400 text-xs">
                                    No amenities configured for this participant.
                                </div>
                            )}
                        </div>

                        {/* Scan next button */}
                        <div className="px-5 pb-4">
                            <button
                                onClick={() => {
                                    setScanResult(null);
                                    setScanError('');
                                    setTimeout(() => inputRef.current?.focus(), 50);
                                }}
                                className="w-full py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-bold rounded-xl transition-colors"
                            >
                                ← Scan Next Participant
                            </button>
                        </div>
                    </div>
                )}

                {/* Empty state */}
                {!scanResult && !scanning && !scanError && (
                    <div className="text-center py-12 text-gray-300">
                        <div className="text-6xl mb-4">📷</div>
                        <p className="text-sm font-medium">Ready to scan</p>
                        <p className="text-xs mt-1">Point a QR scanner or enter a participant ID above</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EventScanPageV2;
